import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useUser } from '../UserContext';
import api from '../api';
import MoaniDisplay from './MoaniDisplay';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
  Alert,
  Divider,
  Stack,
  TextField,
  Button,
  Collapse,
  IconButton,
  Link
} from '@mui/material';
import {
  Search as SearchIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Warning as WarningIcon,
  Person as PersonIcon,
  Block as BlockIcon,
  Report as ReportIcon
} from '@mui/icons-material';

function AuctionHouseDetective({ userId }) {
  const navigate = useNavigate();
  const { selectUserById } = useUser();
  const [maxValueThreshold, setMaxValueThreshold] = useState(1);
  const [expandedUsers, setExpandedUsers] = useState(new Set());

  // Get user data for display name
  const { data: userData } = useQuery({
    queryKey: ['user', userId],
    queryFn: async () => {
      if (!userId) return null;
      const response = await api.get(`/users/${userId}`);
      return response.data;
    },
    enabled: !!userId,
    staleTime: 60000 
  });

  const { data: detectiveData, isLoading, error, refetch } = useQuery({
    queryKey: ['auctionHouseDetective', userId, maxValueThreshold],
    queryFn: async () => {
      if (!userId) return null;
      const response = await api.get(`/users/${userId}/auction-house-detective?maxValue=${maxValueThreshold}`);
      return response.data;
    },
    enabled: !!userId,
    staleTime: 60000 
  });

  if (!userId) {
    return (
      <Box display="flex" justifyContent="center" mt={4} sx={{ p: 2 }}>
        <Typography variant="h6">No user ID provided.</Typography>
      </Box>
    );
  }

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" mt={4}>
        <Stack alignItems="center" spacing={2}>
          <CircularProgress />
          <Typography>Analyzing auction house activity...</Typography>
        </Stack>
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mt: 2 }}>
        Failed to load detective analysis: {error.message}
      </Alert>
    );
  }

  const { summary = {}, suspiciousUsers = [] } = detectiveData || {};

  const toggleUserExpanded = (userId) => {
    setExpandedUsers(prev => {
      const newSet = new Set(prev);
      if (newSet.has(userId)) {
        newSet.delete(userId);
      } else {
        newSet.add(userId);
      }
      return newSet;
    });
  };

  const formatDate = (dateString) => {
    return dateString ? new Date(dateString).toLocaleString() : 'N/A';
  };

  const getSuspiciousnessColor = (score) => {
    if (score > 1000) return 'error';
    if (score > 500) return 'warning';
    if (score > 100) return 'info';
    return 'default';
  };

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h4" gutterBottom>
        Auction House Detective - {userData?.name || 'User'}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Detect suspicious low-value trades that may indicate multiaccounting or coordinated activity.
      </Typography>

      {/* Filter Controls */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Stack direction="row" spacing={2} alignItems="center">
          <TextField
            label="Max Value Threshold (MOANI)"
            type="number"
            value={maxValueThreshold}
            onChange={(e) => setMaxValueThreshold(Number(e.target.value))}
            size="small"
            InputProps={{
              inputProps: { min: 0, step: 0.01 }
            }}
            helperText="Only analyze trades below this value"
          />
          <Button
            variant="outlined"
            onClick={() => refetch()}
            disabled={isLoading}
          >
            Refresh Analysis
          </Button>
        </Stack>
      </Paper>

      {/* Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)' }}>
            <CardContent>
              <Stack direction="row" alignItems="center" spacing={2}>
                <WarningIcon sx={{ fontSize: 40, color: '#fff' }} />
                <Box>
                  <Typography color="text.secondary" variant="subtitle2">
                    Suspicious Users
                  </Typography>
                  <Typography variant="h6" color="white">
                    {summary.totalSuspiciousUsers || 0}
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
            <CardContent>
              <Stack direction="row" alignItems="center" spacing={2}>
                <SearchIcon sx={{ fontSize: 40, color: '#fff' }} />
                <Box>
                  <Typography color="text.secondary" variant="subtitle2">
                    Suspicious Trades
                  </Typography>
                  <Typography variant="h6" color="white">
                    {summary.totalSuspiciousTrades || 0}
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)' }}>
            <CardContent>
              <Stack direction="row" alignItems="center" spacing={2}>
                <Box>
                  <Typography color="text.secondary" variant="subtitle2">
                    Total Value
                  </Typography>
                  <MoaniDisplay 
                    amount={summary.totalSuspiciousValue || 0} 
                    isWei={false}
                    variant="h6" 
                    sx={{ color: 'white' }}
                  />
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ background: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)' }}>
            <CardContent>
              <Stack direction="row" alignItems="center" spacing={2}>
                <BlockIcon sx={{ fontSize: 40, color: '#fff' }} />
                <Box>
                  <Typography color="text.secondary" variant="subtitle2">
                    Already Banned/Cheater
                  </Typography>
                  <Typography variant="h6" color="white">
                    {summary.bannedCount || 0} / {summary.cheaterCount || 0}
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Suspicious Users Table */}
      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>
          Suspicious Trading Partners
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Users with multiple low-value trades (sorted by suspiciousness score)
        </Typography>
        <Divider sx={{ mb: 2 }} />
        
        {suspiciousUsers.length === 0 ? (
          <Alert severity="info" sx={{ mt: 2 }}>
            No suspicious trading patterns found with the current threshold.
          </Alert>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>User</TableCell>
                  <TableCell>Trade Count</TableCell>
                  <TableCell>Avg Value</TableCell>
                  <TableCell>Median Value</TableCell>
                  <TableCell>Min/Max Value</TableCell>
                  <TableCell>Total Value</TableCell>
                  <TableCell>Suspiciousness</TableCell>
                  <TableCell>First/Last Trade</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {suspiciousUsers.map((user) => {
                  const isExpanded = expandedUsers.has(user.counterPartyId);
                  
                  return (
                    <React.Fragment key={user.counterPartyId}>
                      <TableRow hover>
                        <TableCell>
                          <Button
                            size="small"
                            onClick={() => {
                              selectUserById(user.counterPartyId, navigate);
                            }}
                            sx={{ textTransform: 'none' }}
                            startIcon={<PersonIcon />}
                          >
                            {user.counterPartyName || user.counterPartyWallet?.slice(0, 8) || 'Unknown'}
                          </Button>
                          {user.counterPartyWallet && (
                            <Typography variant="caption" color="text.secondary" display="block">
                              {user.counterPartyWallet.slice(0, 6)}...{user.counterPartyWallet.slice(-4)}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight="bold">
                            {user.count}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <MoaniDisplay 
                            amount={user.avgValue} 
                            isWei={false}
                            variant="body2"
                          />
                        </TableCell>
                        <TableCell>
                          <MoaniDisplay 
                            amount={user.medianValue} 
                            isWei={false}
                            variant="body2"
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                            <MoaniDisplay 
                              amount={user.minValue} 
                              isWei={false}
                              variant="caption"
                            />
                            {' / '}
                            <MoaniDisplay 
                              amount={user.maxValue} 
                              isWei={false}
                              variant="caption"
                            />
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <MoaniDisplay 
                            amount={user.totalValue} 
                            isWei={false}
                            variant="body2"
                            fontWeight="bold"
                          />
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={user.suspiciousnessScore.toFixed(0)}
                            size="small"
                            color={getSuspiciousnessColor(user.suspiciousnessScore)}
                            icon={<WarningIcon />}
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption" display="block">
                            {formatDate(user.firstTrade)}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" display="block">
                            {formatDate(user.lastTrade)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Stack direction="row" spacing={0.5}>
                            {user.counterPartyBanned && (
                              <Chip label="Banned" size="small" color="error" />
                            )}
                            {user.counterPartyCheater && (
                              <Chip label="Cheater" size="small" color="warning" />
                            )}
                            {!user.counterPartyBanned && !user.counterPartyCheater && (
                              <Chip label="Active" size="small" color="success" />
                            )}
                          </Stack>
                        </TableCell>
                        <TableCell>
                          <IconButton
                            size="small"
                            onClick={() => toggleUserExpanded(user.counterPartyId)}
                          >
                            {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                          </IconButton>
                        </TableCell>
                      </TableRow>
                      {isExpanded && (
                        <TableRow>
                          <TableCell colSpan={10} sx={{ py: 2, backgroundColor: 'background.default' }}>
                            <Typography variant="subtitle2" gutterBottom>
                              Trade Details ({user.transactions.length} trades)
                            </Typography>
                            <TableContainer>
                              <Table size="small">
                                <TableHead>
                                  <TableRow>
                                    <TableCell>Date</TableCell>
                                    <TableCell>Type</TableCell>
                                    <TableCell>Item</TableCell>
                                    <TableCell>Quantity</TableCell>
                                    <TableCell>Unit Price</TableCell>
                                    <TableCell>Total Value</TableCell>
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {user.transactions.map((tx) => (
                                    <TableRow key={tx.id}>
                                      <TableCell>
                                        <Typography variant="caption">
                                          {formatDate(tx.updatedAt)}
                                        </Typography>
                                      </TableCell>
                                      <TableCell>
                                        <Chip
                                          label={tx.type === 'sale' ? 'Sale' : 'Purchase'}
                                          size="small"
                                          color={tx.type === 'sale' ? 'success' : 'primary'}
                                        />
                                      </TableCell>
                                      <TableCell>
                                        <Typography variant="caption">
                                          {tx.assetTitle || 'Unknown'}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary" display="block">
                                          {tx.assetId?.slice(0, 8)}...
                                        </Typography>
                                      </TableCell>
                                      <TableCell>
                                        <Typography variant="caption">
                                          {tx.stackSize}
                                        </Typography>
                                      </TableCell>
                                      <TableCell>
                                        <MoaniDisplay 
                                          amount={Number(tx.unitPrice) / 1e18} 
                                          isWei={false}
                                          variant="caption"
                                        />
                                      </TableCell>
                                      <TableCell>
                                        <MoaniDisplay 
                                          amount={tx.totalValue} 
                                          isWei={false}
                                          variant="caption"
                                          fontWeight="bold"
                                        />
                                      </TableCell>
                                    </TableRow>
                                  ))}
                                </TableBody>
                              </Table>
                            </TableContainer>
                          </TableCell>
                        </TableRow>
                      )}
                    </React.Fragment>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>
    </Box>
  );
}

export default AuctionHouseDetective;



